import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export type ContextMenuItem = {
  id: string;
  label: string;
  detail?: string;
  shortcut?: string;
  danger?: boolean;
  disabled?: boolean;
  current?: boolean;
  closeOnSelect?: boolean;
  onSelect: () => void;
};

export type ContextMenuSection = {
  id: string;
  label?: string;
  items: readonly ContextMenuItem[];
};

export function ContextMenu({
  ariaLabel,
  onClose,
  position,
  sections,
}: {
  ariaLabel: string;
  onClose: () => void;
  position: { x: number; y: number };
  sections: readonly ContextMenuSection[];
}) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [clampedPosition, setClampedPosition] = useState(position);

  useLayoutEffect(() => {
    const menu = menuRef.current;
    if (!menu) return;
    const margin = 8;
    const bounds = menu.getBoundingClientRect();
    setClampedPosition({
      x: Math.max(margin, Math.min(position.x, window.innerWidth - bounds.width - margin)),
      y: Math.max(margin, Math.min(position.y, window.innerHeight - bounds.height - margin)),
    });
    menu.querySelector<HTMLButtonElement>("button:not(:disabled)")?.focus({ preventScroll: true });
  }, [position]);

  useEffect(() => {
    const closeFromOutside = (event: PointerEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) onClose();
    };
    const close = () => onClose();
    const closeFromScroll = (event: Event) => {
      if (!menuRef.current?.contains(event.target as Node)) onClose();
    };
    window.addEventListener("pointerdown", closeFromOutside, true);
    window.addEventListener("resize", close);
    window.addEventListener("scroll", closeFromScroll, true);
    return () => {
      window.removeEventListener("pointerdown", closeFromOutside, true);
      window.removeEventListener("resize", close);
      window.removeEventListener("scroll", closeFromScroll, true);
    };
  }, [onClose]);

  const visibleSections = sections.filter((section) => section.items.length > 0);
  return createPortal(
    <div
      aria-label={ariaLabel}
      className="app-context-menu"
      onContextMenu={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.preventDefault();
          onClose();
          return;
        }
        if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
        const items = [
          ...event.currentTarget.querySelectorAll<HTMLButtonElement>("button:not(:disabled)"),
        ];
        if (items.length === 0) return;
        event.preventDefault();
        const current = items.indexOf(document.activeElement as HTMLButtonElement);
        const nextIndex =
          event.key === "Home"
            ? 0
            : event.key === "End"
              ? items.length - 1
              : (Math.max(0, current) + (event.key === "ArrowDown" ? 1 : -1) + items.length) %
                items.length;
        items[nextIndex]?.focus();
      }}
      ref={menuRef}
      role="menu"
      style={{ left: clampedPosition.x, top: clampedPosition.y }}
    >
      {visibleSections.map((section) => (
        <section className="app-context-menu-section" key={section.id}>
          {section.label && <strong>{section.label}</strong>}
          {section.items.map((item) => (
            <button
              aria-current={item.current ? "true" : undefined}
              className={item.danger ? "is-danger" : undefined}
              disabled={item.disabled}
              key={item.id}
              onClick={() => {
                item.onSelect();
                if (item.closeOnSelect !== false) onClose();
              }}
              role="menuitem"
              type="button"
            >
              <span>{item.label}</span>
              {(item.detail || item.shortcut) && <small>{item.detail ?? item.shortcut}</small>}
            </button>
          ))}
        </section>
      ))}
    </div>,
    document.body,
  );
}
